import { useList, type HttpError } from "@refinedev/core";
import { useMemo } from "react";
import { resolveItemLabel } from "../utils/displayUtils";

export interface RelatedItemOption {
  label: string;
  value: string;
  /** Additional context fields shown as secondary text in dropdowns */
  description?: string;
}

interface RelatedItemConfig {
  /** The Aras ItemType name (e.g., "Organization", "Person") */
  itemType: string;
  /** Field name to use as label (e.g., "name", "organizationName") */
  labelField?: string;
  /** Field name to use as value (defaults to "id") */
  valueField?: string;
}

interface UseRelatedItemsOptions {
  /** Array of related item configurations to fetch */
  relatedItems: RelatedItemConfig[];
  /** Whether to enable the fetch */
  enabled?: boolean;
}

interface UseRelatedItemsResult {
  /** Map of itemType -> options array */
  items: Record<string, RelatedItemOption[]>;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
}

/**
 * Get the resource name for an ItemType.
 * Uses exact ItemType name - no conversion.
 */
function getResourceName(itemType: string): string {
  // Use exact ItemType name as resource
  return itemType;
}

/**
 * System item types that should be excluded from related items fetching.
 * These are Aras system types that don't need dropdown population.
 */
const systemItemTypes = new Set([
  "User",
  "Identity",
  "Life Cycle State",
  "Permission",
  "Team",
  "Workflow",
  "Activity",
  "History",
  "File",
]);

/**
 * Exact field names to always skip in description.
 * These are Aras system/identity fields that never provide useful context.
 */
const descriptionSkipExact = new Set([
  "id",
  "keyedName",
  "itemNumber",
  "state",
  "notes",
]);

/**
 * Check if a field name is a system/internal field by naming pattern.
 * Catches all Aras system fields generically without hardcoding every name.
 */
function isSystemFieldName(key: string): boolean {
  if (descriptionSkipExact.has(key)) return true;

  const lower = key.toLowerCase();
  // *Id fields (createdById, ownedById, organizationId, siteId, etc.) — GUIDs
  if (lower.endsWith("id")) return true;
  // *Display fields (createdByIdDisplay, currentStateDisplay) — system display labels
  if (lower.endsWith("display")) return true;
  // *On fields (createdOn, modifiedOn) — timestamps
  if (lower.endsWith("on") && (lower.startsWith("created") || lower.startsWith("modified")))
    return true;
  // currentState — GUID
  if (lower === "currentstate") return true;

  return false;
}

/**
 * Check if a value looks like a GUID, date, or other internal Aras value.
 */
function isInternalValue(val: string): boolean {
  const trimmed = val.trim();
  // GUIDs (with or without dashes)
  if (/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(trimmed))
    return true;
  if (/^[0-9a-f]{32}$/i.test(trimmed)) return true;
  // Aras identity names like "Identity_Site_HCM_VP"
  if (/^Identity_/i.test(trimmed)) return true;
  // ISO dates
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) return true;
  return false;
}

/**
 * Substrings that indicate a high-value business field when found in the key name.
 * Fields matching these patterns are shown first in descriptions.
 * Case-insensitive matching.
 */
const highPriorityPatterns = [
  "name",
  "phone",
  "email",
  "org",
  "company",
  "address",
  "city",
  "source",
  "website",
];

/**
 * Get the priority score for a field name.
 * Lower = higher priority. Fields matching common business patterns come first.
 */
function getFieldPriority(key: string): number {
  const lower = key.toLowerCase();
  for (let i = 0; i < highPriorityPatterns.length; i++) {
    if (lower.includes(highPriorityPatterns[i])) return i;
  }
  return highPriorityPatterns.length; // unmatched fields come last
}

/**
 * Build a short description string from an item's fields.
 * Shows up to 3 extra fields that aren't the label, providing
 * additional context for dropdown selection.
 *
 * Strategy:
 * 1. Filter out system fields by naming pattern (blacklist)
 * 2. Sort remaining fields by priority (name/phone/email first)
 * 3. Pick the first 3 meaningful string values
 *
 * This works for any ItemType without knowing field names in advance.
 */
function buildItemDescription(
  item: Record<string, unknown>,
  labelValue: string
): string {
  const usedValues = new Set([labelValue]);

  // Collect candidate fields: non-system, non-empty string values
  const candidates: { key: string; val: string }[] = [];
  for (const [key, rawVal] of Object.entries(item)) {
    if (isSystemFieldName(key)) continue;
    if (typeof rawVal !== "string") continue;

    const val = rawVal.trim();
    if (val && val.length <= 80 && !usedValues.has(val) && !isInternalValue(val)) {
      candidates.push({ key, val });
      usedValues.add(val);
    }
  }

  // Sort by priority: name/phone/email/org fields first
  candidates.sort((a, b) => getFieldPriority(a.key) - getFieldPriority(b.key));

  return candidates
    .slice(0, 3)
    .map((c) => c.val)
    .join(" · ");
}

/**
 * Hook to fetch related items for 'item' dataType fields.
 * Used for populating dropdown options in forms for relationship fields.
 *
 * @param relatedItems - Array of item type configurations to fetch
 * @param enabled - Whether to enable the fetch
 */
export const useRelatedItems = ({
  relatedItems,
  enabled = true,
}: UseRelatedItemsOptions): UseRelatedItemsResult => {
  // Get unique item types to fetch, excluding system types
  const uniqueItemTypes = useMemo(() => {
    const types = new Set(
      relatedItems
        .map((r) => r.itemType)
        .filter((type) => !systemItemTypes.has(type))
    );
    return Array.from(types);
  }, [relatedItems]);

  // We need to fetch each item type separately
  // Support up to 8 different item types (Lead has 6 reference fields)
  const itemType0 = uniqueItemTypes[0];
  const itemType1 = uniqueItemTypes[1];
  const itemType2 = uniqueItemTypes[2];
  const itemType3 = uniqueItemTypes[3];
  const itemType4 = uniqueItemTypes[4];
  const itemType5 = uniqueItemTypes[5];
  const itemType6 = uniqueItemTypes[6];
  const itemType7 = uniqueItemTypes[7];

  const shouldFetch = enabled && uniqueItemTypes.length > 0;

  // Common query options for all useList calls
  // retry: false prevents infinite retries on 400/500 errors
  // This allows graceful degradation when an item type isn't configured in Aras
  const commonQueryOptions = {
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    retry: false, // Don't retry on errors (e.g., 400 Bad Request)
  };

  // Fetch each item type using useList
  const {
    data: data0,
    isLoading: loading0,
    isError: error0,
    error: err0,
  } = useList({
    resource: itemType0 ? getResourceName(itemType0) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType0,
    },
  });

  const {
    data: data1,
    isLoading: loading1,
    isError: error1,
    error: err1,
  } = useList({
    resource: itemType1 ? getResourceName(itemType1) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType1,
    },
  });

  const {
    data: data2,
    isLoading: loading2,
    isError: error2,
    error: err2,
  } = useList({
    resource: itemType2 ? getResourceName(itemType2) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType2,
    },
  });

  const {
    data: data3,
    isLoading: loading3,
    isError: error3,
    error: err3,
  } = useList({
    resource: itemType3 ? getResourceName(itemType3) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType3,
    },
  });

  const {
    data: data4,
    isLoading: loading4,
    isError: error4,
    error: err4,
  } = useList({
    resource: itemType4 ? getResourceName(itemType4) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType4,
    },
  });

  const {
    data: data5,
    isLoading: loading5,
    isError: error5,
    error: err5,
  } = useList({
    resource: itemType5 ? getResourceName(itemType5) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType5,
    },
  });

  const {
    data: data6,
    isLoading: loading6,
    isError: error6,
    error: err6,
  } = useList({
    resource: itemType6 ? getResourceName(itemType6) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType6,
    },
  });

  const {
    data: data7,
    isLoading: loading7,
    isError: error7,
    error: err7,
  } = useList({
    resource: itemType7 ? getResourceName(itemType7) : "",
    pagination: { pageSize: 500 },
    queryOptions: {
      ...commonQueryOptions,
      enabled: shouldFetch && !!itemType7,
    },
  });

  // Combine all results into a single map
  const items = useMemo(() => {
    const result: Record<string, RelatedItemOption[]> = {};

    const dataMap: Record<string, typeof data0> = {};
    if (itemType0 && data0) dataMap[itemType0] = data0;
    if (itemType1 && data1) dataMap[itemType1] = data1;
    if (itemType2 && data2) dataMap[itemType2] = data2;
    if (itemType3 && data3) dataMap[itemType3] = data3;
    if (itemType4 && data4) dataMap[itemType4] = data4;
    if (itemType5 && data5) dataMap[itemType5] = data5;
    if (itemType6 && data6) dataMap[itemType6] = data6;
    if (itemType7 && data7) dataMap[itemType7] = data7;

    for (const config of relatedItems) {
      const { itemType, labelField, valueField = "id" } = config;
      const data = dataMap[itemType];

      if (!data?.data) {
        result[itemType] = [];
        continue;
      }

      // Map items to options using generic label resolution + description
      result[itemType] = data.data.map((item: Record<string, unknown>) => {
        const label = resolveItemLabel(item, labelField);
        return {
          label,
          value: (item[valueField] as string) || (item.id as string) || "",
          description: buildItemDescription(item, label),
        };
      });
    }

    return result;
  }, [
    relatedItems,
    data0,
    data1,
    data2,
    data3,
    data4,
    data5,
    data6,
    data7,
    itemType0,
    itemType1,
    itemType2,
    itemType3,
    itemType4,
    itemType5,
    itemType6,
    itemType7,
  ]);

  // Only report loading if we're actually fetching something
  const isLoading =
    shouldFetch &&
    ((!!itemType0 && loading0) ||
      (!!itemType1 && loading1) ||
      (!!itemType2 && loading2) ||
      (!!itemType3 && loading3) ||
      (!!itemType4 && loading4) ||
      (!!itemType5 && loading5) ||
      (!!itemType6 && loading6) ||
      (!!itemType7 && loading7));

  const isError =
    error0 ||
    error1 ||
    error2 ||
    error3 ||
    error4 ||
    error5 ||
    error6 ||
    error7;
  const error =
    err0 || err1 || err2 || err3 || err4 || err5 || err6 || err7 || null;

  return {
    items,
    isLoading,
    isError,
    error,
  };
};
